import { authenticatedFetch } from "./apiClient";
import type {
  JobSchedule,
  JobStep,
} from "@/schemas/firestore/scheduledJob.schema";

const API_BASE_URL = import.meta.env.VITE_API_URL;

/**
 * Writes for scheduled jobs (design §8).
 *
 * There is deliberately no list or get method: both collections are read
 * direct-from-Firestore (ADR-0003), so a listing call would be a second way to
 * answer the same question — and a worse one, because the subscription is what
 * makes a run finishing show up without a poll.
 *
 * No `owner_uid` is ever sent. core stamps it from the verified caller and
 * ignores one in the body (ADR-0002); on update it is not sent at all, because
 * editing a job does not transfer whose authority its runs execute with.
 */
export interface ScheduledJobInput {
  display_label: string;
  steps: JobStep[];
  schedule: JobSchedule | null;
}

const jobsPath = (orgId: string, domainId: string) =>
  `${API_BASE_URL}/v1/organizations/${orgId}/domains/${domainId}/jobs`;

const trimValues = (record: Record<string, string>): Record<string, string> =>
  Object.fromEntries(
    Object.entries(record).map(([key, value]) => [key, value.trim()]),
  );

/**
 * Trim every id and argument before it goes on the wire.
 *
 * A padded id is not a near-miss, it is a **different Firestore document
 * path**. On dev a `page_id` of `"0b2ddb713d27f5d51b10 "` wrote a heuristic
 * audit to `pages/0b2ddb713d27f5d51b10 /heuristic_jobs/…`, under a page
 * document that does not exist, where nothing would ever read it — and the run
 * reported success (core#101).
 *
 * core strips and rejects blanks too, and it is the authority. This is here
 * because it is the one place both writes funnel through, so no caller — this
 * editor, or whatever calls the service next — can send a padded id by
 * forgetting to trim. A blank one still travels, so core answers 422 with the
 * field it objects to rather than the frontend inventing its own message.
 */
export function normalizeScheduledJobInput(
  input: ScheduledJobInput,
): ScheduledJobInput {
  return {
    ...input,
    display_label: input.display_label.trim(),
    steps: input.steps.map((step) =>
      step.type === "tool"
        ? { ...step, tool: step.tool.trim(), args: trimValues(step.args) }
        : {
            ...step,
            recipe_kind: step.recipe_kind.trim(),
            params: trimValues(step.params),
          },
    ),
    schedule: input.schedule
      ? {
          ...input.schedule,
          cron: input.schedule.cron.trim(),
          timezone: input.schedule.timezone.trim(),
        }
      : null,
  };
}

async function throwIfNotOk(response: Response, action: string): Promise<void> {
  if (response.ok) return;
  let detail = response.statusText;
  try {
    const body: unknown = await response.json();
    if (
      typeof body === "object" &&
      body !== null &&
      typeof (body as { detail?: unknown }).detail === "string"
    ) {
      detail = (body as { detail: string }).detail;
    }
  } catch {
    // Non-JSON error body; statusText is good enough.
  }
  // core's messages are written to be read by a person -- "a scheduled job may
  // run at most once an hour", "Unknown tool: x", "step 0 references step 1,
  // which has not run yet". Surface them verbatim rather than replacing them.
  throw new Error(`${action} failed: ${detail}`);
}

export class ScheduledJobService {
  static async create(
    orgId: string,
    domainId: string,
    input: ScheduledJobInput,
  ): Promise<void> {
    const response = await authenticatedFetch(jobsPath(orgId, domainId), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(normalizeScheduledJobInput(input)),
    });
    await throwIfNotOk(response, "Saving the job");
  }

  /**
   * A full replacement, not a patch: core's PUT clears whatever the body omits.
   * The editor always sends the complete job for that reason.
   */
  static async update(
    orgId: string,
    domainId: string,
    jobId: string,
    input: ScheduledJobInput,
  ): Promise<void> {
    const response = await authenticatedFetch(
      `${jobsPath(orgId, domainId)}/${jobId}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(normalizeScheduledJobInput(input)),
      },
    );
    await throwIfNotOk(response, "Saving the job");
  }

  static async remove(
    orgId: string,
    domainId: string,
    jobId: string,
  ): Promise<void> {
    const response = await authenticatedFetch(
      `${jobsPath(orgId, domainId)}/${jobId}`,
      { method: "DELETE" },
    );
    await throwIfNotOk(response, "Deleting the job");
  }

  /**
   * Run now. Returns as soon as the first step is queued — core answers 202 and
   * the run itself happens on core-worker. The runs subscription reports the
   * outcome, so there is nothing to poll here.
   *
   * The presser is recorded as `triggered_by_uid`, but the run still executes
   * with the job **owner's** authority: one job, one authority, whether a clock
   * or a person started it.
   */
  static async runNow(
    orgId: string,
    domainId: string,
    jobId: string,
  ): Promise<void> {
    const response = await authenticatedFetch(
      `${jobsPath(orgId, domainId)}/${jobId}/run`,
      { method: "POST" },
    );
    await throwIfNotOk(response, "Starting the run");
  }

  /**
   * Arm the platform's default weekly analytics-snapshot job for this domain,
   * creating it if it doesn't already exist.
   *
   * Idempotent on core's side: calling it when the job already exists returns
   * the existing job untouched. Exempt from `MAX_JOBS_PER_DOMAIN` and the
   * admin-only schedule gate, so an editor may call this on first connect.
   */
  static async ensureAnalyticsSnapshotJob(
    orgId: string,
    domainId: string,
  ): Promise<void> {
    const response = await authenticatedFetch(
      `${jobsPath(orgId, domainId)}/defaults/analytics-snapshot`,
      { method: "POST" },
    );
    await throwIfNotOk(response, "Arming the analytics snapshot job");
  }
}
